/**
 * Minimal card component for displaying a saved session
 *
 * Shows session code (copyable), player name, and a state-dependent action button.
 * Derives connection status from activeConnectionsStore using deterministic endpoint IDs.
 */

import { Copy, Check, MoreVertical, Trash2, Loader2, Unplug } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { CONNECTION_ACTIONS } from '@/constants/terminology';
import { cn } from '@/lib/utils';
import { getEndpointIdsForSession } from '@/services/sessionAdapter';
import { useActiveConnectionsStore } from '@/stores/activeConnectionsStore';
import type { ConnectionStatus } from '@/types/endpoint';
import type { SavedSession } from '@/types/session';

export interface SessionCardProps {
  /** The session to display */
  session: SavedSession;
  /** Handler for connecting the session */
  onConnect: (sessionId: string) => void;
  /** Handler for disconnecting the session */
  onDisconnect: (sessionId: string) => void;
  /** Handler for deleting the session */
  onDelete: (sessionId: string) => void;
}

/**
 * Get the aggregate connection status for a session
 *
 * Checks all derived endpoint IDs against the active connections store.
 *
 * @param sessionId - The session ID
 * @returns The aggregate connection status
 */
function useSessionConnectionStatus(sessionId: string): ConnectionStatus {
  const { connections } = useActiveConnectionsStore();
  const endpointIds = getEndpointIdsForSession(sessionId);

  for (const id of endpointIds) {
    const state = connections.get(id);
    if (state?.status === 'connected') return 'connected';
    if (state?.status === 'connecting') return 'connecting';
    if (state?.status === 'error') return 'error';
  }

  return 'disconnected';
}

/**
 * Compact card displaying a saved session with connection status and actions
 *
 * @example
 * ```tsx
 * <SessionCard
 *   session={savedSession}
 *   onConnect={handleConnect}
 *   onDisconnect={handleDisconnect}
 *   onDelete={handleDelete}
 * />
 * ```
 */
export function SessionCard({
  session,
  onConnect,
  onDisconnect,
  onDelete
}: SessionCardProps) {
  const [copied, setCopied] = useState(false);
  const connectionStatus = useSessionConnectionStatus(session.id);
  const isConnected = connectionStatus === 'connected';
  const sessionCode = session.config.sessionCode;
  const playerName = session.config.playerName;

  /**
   * Copy the session code to clipboard
   */
  async function handleCopyCode() {
    try {
      await navigator.clipboard.writeText(sessionCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  }

  return (
    <Card
      variant={isConnected ? 'elevated' : 'default'}
      role="article"
      aria-label={`Session: ${sessionCode}, Player: ${playerName}, Status: ${connectionStatus}`}
      className={cn(
        'relative p-3 transition-all',
        isConnected && 'border-primary/50',
        !isConnected && 'hover:border-primary/30 hover:shadow-[0_0_10px_hsl(var(--glow)/0.1)]'
      )}
    >
      <div className="flex items-center gap-3">
        {/* Session Code + Player Name */}
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={handleCopyCode}
            className={cn(
              'flex items-center gap-1.5 font-mono font-medium text-sm transition-colors shrink-0',
              isConnected ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
            title="Copy session code"
          >
            {sessionCode}
            {copied ? (
              <Check className="h-3.5 w-3.5 text-green-400" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>

          <span className="text-xs text-muted-foreground truncate">
            {playerName}
          </span>
        </div>

        <div className="flex-1" />

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {connectionStatus === 'connected' && (
            <Button
              size="sm"
              variant="secondary-action"
              onClick={() => onDisconnect(session.id)}
              className="h-8 px-3 text-xs"
              enablePressEffect
            >
              <Unplug className="h-4 w-4 mr-1" />
              {CONNECTION_ACTIONS.DISCONNECT}
            </Button>
          )}

          {connectionStatus === 'disconnected' && (
            <Button
              size="sm"
              variant="secondary-action"
              onClick={() => onConnect(session.id)}
              className="h-8 px-3 text-xs"
              enablePressEffect
            >
              {CONNECTION_ACTIONS.CONNECT}
            </Button>
          )}

          {connectionStatus === 'connecting' && (
            <Button size="sm" variant="outline" disabled className="h-8 px-3 text-xs">
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              {CONNECTION_ACTIONS.RECONNECTING}
            </Button>
          )}

          {connectionStatus === 'error' && (
            <Button
              size="sm"
              variant="secondary-action"
              onClick={() => onConnect(session.id)}
              className="h-8 px-3 text-xs"
              enablePressEffect
            >
              {CONNECTION_ACTIONS.RETRY}
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{sessionCode}</DropdownMenuLabel>
              <DropdownMenuLabel className="font-normal text-muted-foreground">
                Playing as: {playerName}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(session.id)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </Card>
  );
}
